import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/Select';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { TaskPriority, TaskStatus } from '../../../core/models/Task';
import { formatTaskVersion } from '../../../core/constants/taskVersionMilestone';
import { X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../../components/ui/DropdownMenu';

const ALL = '__all__';

export interface TaskFilters {
  clientId: string;
  projectId: string;
  label: string;
  version: string;
  priority: '' | TaskPriority;
  status: TaskStatus[];
}

const DEFAULT_FILTERS: TaskFilters = {
  clientId: '',
  projectId: '',
  label: '',
  version: '',
  priority: '',
  status: [],
};

const STATUS_OPTIONS: Array<{ value: TaskStatus; label: string }> = [
  { value: TaskStatus.TODO, label: 'To Do' },
  { value: TaskStatus.IN_PROGRESS, label: 'In Progress' },
  { value: TaskStatus.IN_REVIEW, label: 'In Review' },
  { value: TaskStatus.BLOCKED, label: 'Blocked' },
  { value: TaskStatus.DONE, label: 'Done' },
];

const PRIORITY_OPTIONS: Array<{ value: TaskPriority; label: string }> = [
  { value: TaskPriority.LOW, label: 'Low' },
  { value: TaskPriority.MEDIUM, label: 'Medium' },
  { value: TaskPriority.HIGH, label: 'High' },
  { value: TaskPriority.URGENT, label: 'Urgent' },
];

export interface TaskFiltersBarProps {
  isSuperAdmin: boolean;
  clientAccounts: Array<{ id: string; name: string }>;
  /** Projects (from useClients — each row is a project under a client account). */
  projects: Array<{ id: string; name: string; clientId: string }>;
  labels: string[];
  versions: string[];
  filters: TaskFilters;
  onApplyFilters: (filters: TaskFilters) => void;
  onClearFilters: () => void;
}

export const TaskFiltersBar: React.FC<TaskFiltersBarProps> = ({
  isSuperAdmin,
  clientAccounts,
  projects,
  labels,
  versions,
  filters,
  onApplyFilters,
  onClearFilters,
}) => {
  const [draftFilters, setDraftFilters] = useState<TaskFilters>(filters);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setDraftFilters(filters);
  }, [filters]);

  const setDraftFilter = <K extends keyof TaskFilters>(key: K, value: TaskFilters[K]) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const statusOrder = useMemo(
    () =>
      STATUS_OPTIONS.reduce((acc, option, index) => {
        acc[option.value] = index;
        return acc;
      }, {} as Record<TaskStatus, number>),
    []
  );

  const normalizeStatuses = useCallback(
    (values: TaskStatus[]) =>
      Array.from(new Set(values))
        .filter((value) => STATUS_OPTIONS.some((statusOption) => statusOption.value === value))
        .sort((a, b) => statusOrder[a] - statusOrder[b]),
    [statusOrder]
  );

  const projectOptions = useMemo(() => {
    if (!draftFilters.clientId) {
      return projects;
    }
    return projects.filter((p) => p.clientId === draftFilters.clientId);
  }, [draftFilters.clientId, projects]);

  useEffect(() => {
    if (!draftFilters.projectId) return;
    if (!projectOptions.some((p) => p.id === draftFilters.projectId)) {
      setDraftFilter('projectId', '');
    }
  }, [draftFilters.projectId, projectOptions]);

  const hasDraftChanges = useMemo(() => {
    const appliedStatuses = normalizeStatuses(filters.status);
    const draftStatuses = normalizeStatuses(draftFilters.status);
    const statusChanged =
      appliedStatuses.length !== draftStatuses.length ||
      appliedStatuses.some((status, index) => status !== draftStatuses[index]);
    return (
      filters.clientId !== draftFilters.clientId ||
      filters.projectId !== draftFilters.projectId ||
      filters.label !== draftFilters.label ||
      filters.version !== draftFilters.version ||
      filters.priority !== draftFilters.priority ||
      statusChanged
    );
  }, [draftFilters, filters, normalizeStatuses]);

  const hasAnyAppliedFilter = useMemo(() => {
    return !!(
      filters.clientId ||
      filters.projectId ||
      filters.label ||
      filters.version ||
      filters.priority ||
      filters.status.length > 0
    );
  }, [filters]);

  const clientNameById = useMemo(() => {
    return clientAccounts.reduce((acc, account) => {
      acc[account.id] = account.name;
      return acc;
    }, {} as Record<string, string>);
  }, [clientAccounts]);

  const projectNameById = useMemo(() => {
    return projects.reduce((acc, project) => {
      acc[project.id] = project.name;
      return acc;
    }, {} as Record<string, string>);
  }, [projects]);

  const activeFilterChips = useMemo(() => {
    const chips: Array<{ key: keyof TaskFilters; label: string; statusValue?: TaskStatus }> = [];
    if (filters.clientId) {
      chips.push({ key: 'clientId', label: `Client: ${clientNameById[filters.clientId] || filters.clientId}` });
    }
    if (filters.projectId) {
      chips.push({ key: 'projectId', label: `Project: ${projectNameById[filters.projectId] || filters.projectId}` });
    }
    if (filters.label) {
      chips.push({ key: 'label', label: `Label: ${filters.label}` });
    }
    if (filters.version) {
      chips.push({ key: 'version', label: `Version: ${formatTaskVersion(filters.version)}` });
    }
    if (filters.priority) {
      chips.push({
        key: 'priority',
        label: `Priority: ${PRIORITY_OPTIONS.find((option) => option.value === filters.priority)?.label || filters.priority}`,
      });
    }
    for (const selectedStatus of normalizeStatuses(filters.status)) {
      chips.push({
        key: 'status',
        statusValue: selectedStatus,
        label: `Status: ${STATUS_OPTIONS.find((option) => option.value === selectedStatus)?.label || selectedStatus}`,
      });
    }
    return chips;
  }, [filters, clientNameById, projectNameById, normalizeStatuses]);

  const clearSingleFilter = (key: keyof TaskFilters, statusValue?: TaskStatus) => {
    const next = { ...filters } as TaskFilters;
    if (key === 'status') {
      next.status = statusValue
        ? filters.status.filter((s) => s !== statusValue)
        : [];
    } else if (key === 'clientId') {
      next.clientId = '';
    } else if (key === 'projectId') {
      next.projectId = '';
    } else if (key === 'label') {
      next.label = '';
    } else if (key === 'version') {
      next.version = '';
    } else if (key === 'priority') {
      next.priority = '';
    }
    if (key === 'clientId') {
      next.projectId = '';
    }
    setDraftFilters(next);
    onApplyFilters(next);
  };

  return (
    <div className="space-y-2">
      {/* Phones keep the filters folded away until asked for. */}
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="w-full justify-between sm:hidden"
        aria-expanded={filtersOpen}
        onClick={() => setFiltersOpen((open) => !open)}
      >
        {filtersOpen ? 'Hide filters' : `Filters${activeFilterChips.length ? ` (${activeFilterChips.length})` : ''}`}
      </Button>
      <div className={`${filtersOpen ? 'flex' : 'hidden'} flex-wrap items-stretch gap-2 sm:flex sm:items-center`}>
        {isSuperAdmin && clientAccounts.length > 0 && (
          <Select
            value={draftFilters.clientId || ALL}
            onValueChange={(v) => {
              setDraftFilter('clientId', v === ALL ? '' : v);
              setDraftFilter('projectId', '');
            }}
          >
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All clients</SelectItem>
              {clientAccounts.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {projects.length > 0 && (
          <Select
            value={draftFilters.projectId || ALL}
            onValueChange={(v) => setDraftFilter('projectId', v === ALL ? '' : v)}
          >
            <SelectTrigger className="w-full sm:w-[220px]">
              <SelectValue placeholder="Project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All projects</SelectItem>
              {projectOptions.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {labels.length > 0 && (
          <Select
            value={draftFilters.label || ALL}
            onValueChange={(v) => setDraftFilter('label', v === ALL ? '' : v)}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Label" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All labels</SelectItem>
              {labels.map((label) => (
                <SelectItem key={label} value={label}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {versions.length > 0 && (
          <Select
            value={draftFilters.version || ALL}
            onValueChange={(v) => setDraftFilter('version', v === ALL ? '' : v)}
          >
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue placeholder="Version" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All versions</SelectItem>
              {versions.map((version) => (
                <SelectItem key={version} value={version}>
                  {formatTaskVersion(version)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select
          value={draftFilters.priority || ALL}
          onValueChange={(v) => setDraftFilter('priority', v === ALL ? '' : (v as TaskPriority))}
        >
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All priorities</SelectItem>
            {PRIORITY_OPTIONS.map((priority) => (
              <SelectItem key={priority.value} value={priority.value}>
                {priority.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="sm" variant="outline" className="w-full sm:w-[170px] justify-between">
              {draftFilters.status.length > 0 ? `Statuses (${draftFilters.status.length})` : 'All statuses'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[220px]">
            {STATUS_OPTIONS.map((statusOption) => {
              const checked = draftFilters.status.includes(statusOption.value);
              return (
                <DropdownMenuCheckboxItem
                  key={statusOption.value}
                  checked={checked}
                  onCheckedChange={(nextChecked) => {
                    const current = normalizeStatuses(draftFilters.status);
                    const nextStatuses = nextChecked
                      ? normalizeStatuses([...current, statusOption.value])
                      : current.filter((status) => status !== statusOption.value);
                    setDraftFilter('status', nextStatuses);
                  }}
                >
                  {statusOption.label}
                </DropdownMenuCheckboxItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          size="sm"
          onClick={() => onApplyFilters(draftFilters)}
          disabled={!hasDraftChanges}
        >
          Apply
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setDraftFilters(DEFAULT_FILTERS);
            onClearFilters();
          }}
          disabled={!hasAnyAppliedFilter && !hasDraftChanges}
        >
          Clear
        </Button>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {activeFilterChips.map((chip) => (
            <Badge key={chip.statusValue ? `${chip.key}-${chip.statusValue}` : chip.key} variant="outline" className="flex items-center gap-1">
              {chip.label}
              <button
                type="button"
                className="rounded-sm p-0.5 hover:bg-gray-100"
                aria-label={`Remove ${chip.label} filter`}
                onClick={() => clearSingleFilter(chip.key, chip.statusValue)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
